import React, { useCallback, useEffect, useState } from 'react';
import { usePolling } from '@/hooks/usePolling';
import {
  Alert,
  Button,
  Card,
  Col,
  Collapse,
  Progress,
  Row,
  Space,
  Spin,
  Tag,
  Typography,
} from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExperimentOutlined,
  HistoryOutlined,
  ReloadOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { bidAnalysisApi, BidAnalysisDto } from '@/api/bidAnalysisApi';
import { feasibilityApi } from '@/api/feasibilityApi';

const { Text, Title, Paragraph } = Typography;

interface Props {
  opportunityId: string;
}

const POLL_INTERVAL = 3000;

const recColor = (rec?: string): string => {
  if (!rec) return '#d9d9d9';
  if (rec === 'BID') return '#52c41a';
  if (rec === 'BID_WITH_CONDITIONS') return '#faad14';
  return '#ff4d4f';
};

const recLabel = (rec?: string): string => {
  if (!rec) return 'Unknown';
  if (rec === 'BID') return 'BID — Go';
  if (rec === 'BID_WITH_CONDITIONS') return 'BID with Conditions';
  return 'NO BID';
};

const scoreColor = (score: number | undefined): string => {
  if (score === undefined || score === null) return '#d9d9d9';
  if (score >= 70) return '#52c41a';
  if (score >= 40) return '#faad14';
  return '#ff4d4f';
};

const BidAnalysisAI: React.FC<Props> = ({ opportunityId }) => {
  const [analysis, setAnalysis] = useState<BidAnalysisDto | null>(null);
  const [allAnalyses, setAllAnalyses] = useState<BidAnalysisDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [triggering, setTriggering] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [allReviewsDone, setAllReviewsDone] = useState(false);
  const fetchLatest = useCallback(async () => {
    try {
      const res = await bidAnalysisApi.getLatest(opportunityId);
      if (res.status === 204) {
        setAnalysis(null);
        return null;
      }
      setAnalysis(res.data);
      if (res.data?.status === 'COMPLETED') {
        setError(null);
      }
      return res.data;
    } catch {
      return null;
    }
  }, [opportunityId]);

  const fetchAllAnalyses = useCallback(async () => {
    try {
      const res = await bidAnalysisApi.getAll(opportunityId);
      setAllAnalyses(res.data ?? []);
    } catch {
      // ignore
    }
  }, [opportunityId]);

  const isRunning = analysis?.status === 'PENDING' || analysis?.status === 'RUNNING';

  // Poll while analyzing; pauses when the tab is hidden, stops on terminal.
  usePolling(
    async () => {
      const data = await fetchLatest();
      if (data && (data.status === 'COMPLETED' || data.status === 'FAILED')) {
        fetchAllAnalyses();
        return false;
      }
    },
    POLL_INTERVAL,
    isRunning,
  );

  useEffect(() => {
    setLoading(true);
    Promise.all([
      fetchLatest(),
      feasibilityApi.getReviews(opportunityId).catch(() => []),
      fetchAllAnalyses(),
    ]).then(([, reviews]) => {
      setLoading(false);
      // Gate B needs the Technical + Delivery reviews; Staffing is Gate C.
      setAllReviewsDone(
        reviews.some((r) => r.reviewType === 'TECHNICAL') &&
        reviews.some((r) => r.reviewType === 'DELIVERY'),
      );
    });
  }, [opportunityId, fetchLatest, fetchAllAnalyses]);

  const handleTrigger = async () => {
    setTriggering(true);
    setError(null);
    try {
      const res = await bidAnalysisApi.trigger(opportunityId);
      setAnalysis(res.data);
      // Polling auto-starts when status is PENDING/RUNNING (see usePolling above).
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Failed to start analysis';
      // If an analysis is already running OR a successful result exists, show it instead of the error
      const latest = await fetchLatest();
      const latestActive = latest && (latest.status === 'PENDING' || latest.status === 'RUNNING');
      const latestDone = latest && latest.status === 'COMPLETED';
      if (!latestActive && !latestDone) {
        setError(msg);
      }
    } finally {
      setTriggering(false);
    }
  };

  const isCompleted = analysis?.status === 'COMPLETED';
  const isFailed = analysis?.status === 'FAILED';

  return (
    <Collapse
      style={{ marginBottom: 16 }}
      items={[
        {
          key: 'bid-ai',
          label: (
            <Space>
              <ExperimentOutlined style={{ color: 'var(--color-purple)' }} />
              <Text strong style={{ fontSize: 13 }}>
                AI Bid/No-Bid Recommendation
              </Text>
              {isCompleted && analysis?.recommendation === 'BID' && (
                <Tag color="success" icon={<CheckCircleOutlined />}>BID — Go</Tag>
              )}
              {isCompleted && analysis?.recommendation === 'NO_BID' && (
                <Tag color="error" icon={<CloseCircleOutlined />}>NO BID</Tag>
              )}
              {isCompleted && analysis?.recommendation === 'BID_WITH_CONDITIONS' && (
                <Tag color="warning" icon={<WarningOutlined />}>BID with Conditions</Tag>
              )}
              {isRunning && <Tag color="processing">Analyzing...</Tag>}
            </Space>
          ),
          children: (
            <div>
              {/* Action button */}
              <div style={{ marginBottom: 16 }}>
                <Button
                  type="primary"
                  icon={isCompleted || isFailed ? <ReloadOutlined /> : <ExperimentOutlined />}
                  loading={triggering || isRunning}
                  onClick={handleTrigger}
                  disabled={isRunning || !allReviewsDone}
                  style={{ background: 'var(--color-purple)', borderColor: 'var(--color-purple)' }}
                >
                  {isRunning
                    ? 'Analysis in Progress...'
                    : isCompleted || isFailed
                      ? 'Re-analyze'
                      : 'Run AI Analysis'}
                </Button>
                {!allReviewsDone && !isCompleted && !isRunning && (
                  <Text type="secondary" style={{ marginLeft: 12, fontSize: 12 }}>
                    Technical &amp; Delivery feasibility reviews must be submitted first
                  </Text>
                )}
              </div>

              {error && (
                <Alert
                  type="error"
                  message={error}
                  closable
                  onClose={() => setError(null)}
                  style={{ marginBottom: 16 }}
                />
              )}

              {loading && !analysis && <Spin tip="Loading..." />}

              {isRunning && (
                <div style={{ textAlign: 'center', padding: '24px 0' }}>
                  <Spin size="large" />
                  <div style={{ marginTop: 12 }}>
                    <Text type="secondary">
                      AI is analyzing feasibility reviews and opportunity data...
                    </Text>
                  </div>
                </div>
              )}

              {isFailed && analysis?.errorMessage && (
                <Alert
                  type="error"
                  message="Analysis Failed"
                  description={analysis.errorMessage}
                  style={{ marginBottom: 16 }}
                />
              )}

              {isCompleted && analysis && (
                <>
                  {/* Recommendation + Confidence */}
                  <Card
                    size="small"
                    style={{
                      marginBottom: 16,
                      background: analysis.recommendation === 'BID' ? 'var(--color-success-light)' : analysis.recommendation === 'BID_WITH_CONDITIONS' ? 'var(--color-warning-light)' : 'var(--color-error-light)',
                      borderColor: analysis.recommendation === 'BID' ? 'var(--color-success)' : analysis.recommendation === 'BID_WITH_CONDITIONS' ? 'var(--color-warning)' : 'var(--color-error)',
                    }}
                  >
                    <Row align="middle" gutter={24}>
                      <Col>
                        <Progress
                          type="circle"
                          percent={analysis.confidence ?? 0}
                          size={80}
                          strokeColor={recColor(analysis.recommendation)}
                          format={(pct) => (
                            <span style={{ fontSize: 16, fontWeight: 700 }}>{pct}%</span>
                          )}
                        />
                      </Col>
                      <Col flex={1}>
                        <div style={{ marginBottom: 4 }}>
                          <Tag
                            color={recColor(analysis.recommendation)}
                            style={{ fontSize: 14, padding: '4px 12px' }}
                          >
                            {recLabel(analysis.recommendation)}
                          </Tag>
                        </div>
                        <Text type="secondary">
                          Confidence: {analysis.confidence}% · Score: {analysis.overallScore?.toFixed(1)}/100
                        </Text>
                      </Col>
                      <Col>
                        <Progress
                          type="circle"
                          percent={Math.round(analysis.overallScore ?? 0)}
                          size={60}
                          strokeColor={scoreColor(analysis.overallScore)}
                          format={(pct) => (
                            <span style={{ fontSize: 13, fontWeight: 600 }}>{pct}</span>
                          )}
                        />
                      </Col>
                    </Row>
                  </Card>

                  {/* Reasoning */}
                  {analysis.reasoning && (
                    <Card size="small" title="Reasoning" style={{ marginBottom: 16 }}>
                      <Paragraph style={{ margin: 0, fontSize: 13, whiteSpace: 'pre-wrap' }}>
                        {analysis.reasoning}
                      </Paragraph>
                    </Card>
                  )}

                  {/* Risk Factors */}
                  {analysis.riskFactors && analysis.riskFactors.length > 0 && (
                    <Card
                      size="small"
                      title={<><WarningOutlined style={{ color: 'var(--color-warning)', marginRight: 8 }} />Risk Factors</>}
                      style={{ marginBottom: 16 }}
                    >
                      <ol style={{ margin: 0, paddingLeft: 20 }}>
                        {analysis.riskFactors.map((risk, i) => (
                          <li key={i} style={{ fontSize: 13, marginBottom: 4 }}>{risk}</li>
                        ))}
                      </ol>
                    </Card>
                  )}

                  {/* Flip-to-Bid Suggestions */}
                  {analysis.flipToBidSuggestions && analysis.flipToBidSuggestions.length > 0 && (
                    <Card
                      size="small"
                      title={
                        <Space>
                          <CheckCircleOutlined style={{ color: 'var(--color-success)' }} />
                          <span>How to Change This to a BID Decision</span>
                        </Space>
                      }
                      style={{
                        marginBottom: 16,
                        borderColor: '#91d5ff',
                        background: 'var(--color-primary-light)',
                      }}
                    >
                      <ol style={{ margin: 0, paddingLeft: 20 }}>
                        {analysis.flipToBidSuggestions.map((s, i) => (
                          <li key={i} style={{ fontSize: 13, marginBottom: 4 }}>{s}</li>
                        ))}
                      </ol>
                    </Card>
                  )}

                  {/* Summary */}
                  {analysis.summary && (
                    <Card size="small" title="Executive Summary" style={{ marginBottom: 16 }}>
                      <Paragraph style={{ margin: 0, fontSize: 13 }}>
                        {analysis.summary}
                      </Paragraph>
                    </Card>
                  )}

                  {/* Published tag + Metadata */}
                  <div style={{ textAlign: 'right' }}>
                    {analysis.knowledgeArticleId && (
                      <Tag color="purple" style={{ marginRight: 8 }}>Saved to Content Library</Tag>
                    )}
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      {analysis.completedAt &&
                        `Completed: ${new Date(analysis.completedAt).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'medium' })}`}
                      {analysis.modelUsed && ` · Model: ${analysis.modelUsed}`}
                      {analysis.tokensUsed != null &&
                        ` · Tokens: ${analysis.tokensUsed.toLocaleString()}`}
                    </Text>
                  </div>
                </>
              )}

              {/* Analysis History */}
              {allAnalyses.length > 1 && (
                <Collapse
                  size="small"
                  style={{ marginTop: 16 }}
                  items={[
                    {
                      key: 'history',
                      label: (
                        <Space>
                          <HistoryOutlined />
                          <Text style={{ fontSize: 12 }}>
                            Previous Analyses ({allAnalyses.length - 1})
                          </Text>
                        </Space>
                      ),
                      children: (
                        <div>
                          {allAnalyses.slice(1).map((prev) => (
                            <Card
                              key={prev.id}
                              size="small"
                              style={{ marginBottom: 8 }}
                            >
                              <Row align="middle" gutter={12}>
                                <Col>
                                  <Tag color={recColor(prev.recommendation)}>
                                    {recLabel(prev.recommendation)}
                                  </Tag>
                                </Col>
                                <Col flex={1}>
                                  <Text type="secondary" style={{ fontSize: 11 }}>
                                    Score: {prev.overallScore?.toFixed(1)}/100
                                    {' · '}Confidence: {prev.confidence}%
                                    {prev.completedAt &&
                                      ` · ${new Date(prev.completedAt).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'medium' })}`}
                                    {prev.triggeredByName &&
                                      ` · by ${prev.triggeredByName}`}
                                  </Text>
                                </Col>
                                {prev.knowledgeArticleId && (
                                  <Col>
                                    <Tag color="purple" style={{ fontSize: 10 }}>
                                      In Library
                                    </Tag>
                                  </Col>
                                )}
                              </Row>
                              {prev.summary && (
                                <Paragraph
                                  style={{ margin: '8px 0 0', fontSize: 12 }}
                                  ellipsis={{ rows: 2, expandable: true }}
                                >
                                  {prev.summary}
                                </Paragraph>
                              )}
                            </Card>
                          ))}
                        </div>
                      ),
                    },
                  ]}
                />
              )}
            </div>
          ),
        },
      ]}
    />
  );
};

export default BidAnalysisAI;
